import parse from 'html-react-parser';
import { ComponentPropsWithoutRef, ComponentType, ReactNode, useEffect, useRef } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';

interface ConfirmationModalProps {
    title: string;
    subTitle?: string;
    subTitle2?: string;
    onSubmit: () => void;
    onClose: () => void;
    isOnSave?:boolean;
    show: boolean;
    icon?: ComponentType<ComponentPropsWithoutRef<'svg'>> | string;
    discardBtnText?: string;
    closeBtnText?: string;
    iconVariant?:
        | 'light'
        | 'dark'
        | 'secondary'
        | 'destructive'
        | 'default'
        | 'success'
        | 'warning'
        | 'lighter-gray';
    btnVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
    /** Custom body content; when set, replaces the default subtitle block. */
    children?: ReactNode;
    /** Disables the confirm / primary action button. */
    isConfirmDisabled?: boolean;
    /** Prevent closing by clicking outside the dialog. */
    preventDismissOutside?: boolean;
    /** Focus the cancel button when the dialog opens. */
    autoFocusCancel?: boolean;
    /**
     * Mass-update confirm-all only:
     * (`tw-pt-6 tw-pb-12`) while keeping the default horizontal `tw-px-*`.
     */
    asymmetricBodyPadding?: boolean;
}

const ConfirmationModal = ({
    title,
    subTitle,
    onSubmit,
    onClose,
    isOnSave = false,
    show,
    icon,
    discardBtnText,
    closeBtnText,
    btnVariant,
    iconVariant,
    subTitle2,
    children,
    isConfirmDisabled = false,
    preventDismissOutside = false,
    autoFocusCancel = false,
    asymmetricBodyPadding = false,
}: ConfirmationModalProps) => {
    const { t } = useTranslation();
    const cancelButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!show || !autoFocusCancel) {
            return;
        }

        const focusTimer = window.setTimeout(() => {
            cancelButtonRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(focusTimer);
    }, [show, autoFocusCancel]);

    // Drive visibility with `open` so Radix can release body scroll/pointer locks.
    // Unmounting while open={true} leaves the page non-interactive until refresh.
    return (
        <div className="tw-mb-2">
            <Dialog
                open={show}
                onOpenChange={(nextOpen) => {
                    if (!nextOpen) {
                        onClose();
                    }
                }}
            >
                <DialogContent
                    variant="small"
                    className={children ? 'tw-w-full tw-max-w-xl' : undefined}
                    onPointerDownOutside={(e) => {
                        if (preventDismissOutside) {
                            e.preventDefault();
                        }
                    }}
                    onInteractOutside={(e) => {
                        if (preventDismissOutside) {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader>
                        <DialogHeaderDefault
                            className={
                                children
                                    ? 'tw-text-t-lg tw-font-bold !tw-whitespace-normal tw-overflow-visible tw-text-clip'
                                    : 'tw-text-t-lg tw-font-bold'
                            }
                        >
                            {t(title)}
                        </DialogHeaderDefault>
                    </DialogHeader>
                    <DialogBody
                        padding={asymmetricBodyPadding ? 'none' : 'default'}
                        className={
                            children
                                ? asymmetricBodyPadding
                                    ? 'tw-flex tw-flex-col tw-gap-6 tw-pt-6 tw-pb-12 tw-px-5 lg:tw-px-8'
                                    : 'tw-flex tw-flex-col tw-gap-6'
                                : undefined
                        }
                    >
                        {children ?? (
                            <DialogDescription className="!tw-text-t-primary lg-font-dynamic">
                                {subTitle && parse(t(subTitle))} <br />
                                {subTitle2 && t(subTitle2)}
                            </DialogDescription>
                        )}
                    </DialogBody>
                    <DialogFooter
                        className={
                            children
                                ? '!tw-flex-row tw-justify-end tw-gap-3 sm:tw-space-x-0'
                                : 'tw-p-3 tw-h-14'
                        }
                    >
                        {isOnSave ? (
                            <Button
                                type="button"
                                onClick={onSubmit}
                                variant="default"
                                className="tw-px-3 tw-py-1 tw-h-8"
                                disabled={isConfirmDisabled}
                                data-testid="confirmation-modal-confirm"
                            >
                                <span className="tw-flex tw-items-center tw-gap-2">
                                    <span className="tw-text-t-sm">
                                        {t('SAVE')}
                                    </span>
                                </span>
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                onClick={onSubmit}
                                variant={btnVariant || (children ? 'default' : 'outline')}
                                className={children ? 'tw-shrink-0' : 'tw-px-3 tw-py-1 tw-h-8'}
                                disabled={isConfirmDisabled}
                                data-testid="confirmation-modal-confirm"
                            >
                                <span className="tw-flex tw-items-center tw-gap-2">
                                    {icon && (
                                        <Icon
                                            icon={icon}
                                            size="sm"
                                            variant={iconVariant || 'light'}
                                        />
                                    )}
                                    <span className={children ? undefined : 'tw-text-t-sm'}>
                                        {t(discardBtnText || 'YES')}
                                    </span>
                                </span>
                            </Button>
                        )}
                        <Button
                            ref={cancelButtonRef}
                            variant="secondary"
                            type="button"
                            onClick={onClose}
                            className={children ? 'tw-shrink-0' : 'tw-px-3 tw-py-1 tw-h-8 tw-text-t-sm'}
                            data-testid="confirmation-modal-cancel"
                        >
                            {t(closeBtnText || 'NO')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ConfirmationModal;
